import { useNavigate } from "react-router-dom";
import { DashboardCorrelationChart } from "./DashboardCorrelationChart";
import { DashboardActivityPanel } from "./DashboardActivityPanel";
import { DashboardKPIs } from "./DashboardKPIs";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAppData } from "@/contexts/AppDataContext";
import { RoutePath } from "@/routes";
import { productApi } from "@/resources/product";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { landscapeApi } from "@/resources/landscape";
import { BrandSignalsKPIs } from "@/components/shared/BrandSignalsKPIs";
import { EmptyBrandState } from "@/components/shared/EmptyBrandState";
import { PageTitle } from "@/components/shared/PageTitle";

export function DashboardContent() {
  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();

  const { data: brandAsins = [] } = productApi.useGetHeroProductList(selectedBrandId);
  const hasData = brandAsins.length > 0;

  const { data: rankReports = [] } = rankTrackerApi.useGetRankTrackerReports(selectedBrandId);

  const { data: overviewStats } = landscapeApi.useGetProductOverviewStats(selectedBrandId);

  const { data: recentLandscapesData } = landscapeApi.useGetRecentLandscapesForDashboard(selectedBrandId, 30);
  const recentLandscapes = (recentLandscapesData?.landscapes ?? []).slice(0, 3);

  const latestReport = rankReports[0] ?? null;
  const gains = (latestReport?.gains ?? []).slice(0, 3).map((g) => ({
    keyword: g.keyword,
    oldRank: g.from,
    newRank: g.to,
    change: g.from - g.to,
  }));
  const declines = (latestReport?.declines ?? []).slice(0, 3).map((d) => ({
    keyword: d.keyword,
    oldRank: d.from,
    newRank: d.to,
    change: d.to - d.from,
  }));

  if (!hasData) {
    return (
      <div className="space-y-6">
        <div>
          <PageTitle title="Brand Overview" brandScoped />
          <p className="text-sm text-muted-foreground">Brand-wide performance overview and activity</p>
        </div>
        <EmptyBrandState pageName="Dashboard" showLandscapeCta skeleton="dashboard" description="Add Hero ASINs and create your first Keyword Landscape to see performance data, rank movers, and sales correlations." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <PageTitle title="Brand Overview" brandScoped />
        <p className="text-sm text-muted-foreground">Brand-wide performance overview and activity</p>
      </div>

      <DashboardKPIs />

      <div className="flex flex-col xl:flex-row gap-6">
        <div className="flex-1 min-w-0 space-y-6">
          <DashboardCorrelationChart />

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Rank Movers</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate(RoutePath.RankTracker)}>
                  View Rank Tracker
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm font-semibold text-foreground">Biggest Gains</span>
                  </div>
                  <div className="space-y-0">
                    {gains.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-3">No rank gains yet</p>
                    ) : gains.map((m, i) => (
                      <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                        <p className="text-sm text-foreground">"{m.keyword}"</p>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground">
                            #{m.oldRank} → #{m.newRank}
                          </span>
                          <Badge className="border-0 bg-status-success-soft text-foreground font-medium gap-1">
                            <TrendingUp className="h-3 w-3" />
                            +{m.change}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm font-semibold text-foreground">Notable Declines</span>
                  </div>
                  <div className="space-y-0">
                    {declines.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-3">No rank declines yet</p>
                    ) : declines.map((m, i) => (
                      <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                        <p className="text-sm text-foreground">"{m.keyword}"</p>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground">
                            #{m.oldRank} → #{m.newRank}
                          </span>
                          <Badge className="border-0 bg-status-error-soft text-foreground font-medium gap-1">
                            <TrendingDown className="h-3 w-3" />
                            -{m.change}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Brand Signals</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate(RoutePath.ABTests)}>
                  View Brand Signals
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <BrandSignalsKPIs />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Products Overview</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate(RoutePath.Products)}>
                  View Products
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg border border-border p-4 text-center space-y-1">
                  <p className="text-2xl font-semibold text-foreground">{brandAsins.length}</p>
                  <p className="text-xs text-muted-foreground">Hero Products</p>
                </div>
                <div className="rounded-lg border border-border p-4 text-center space-y-1">
                  <p className="text-2xl font-semibold text-foreground">
                    {overviewStats?.landscape_count ?? 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Landscapes</p>
                </div>
                <div className="rounded-lg border border-border p-4 text-center space-y-1">
                  <p className="text-2xl font-semibold text-foreground">{overviewStats?.recommendation_count ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Signal AI Recommendations</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Recently Generated Landscapes</CardTitle>
            </CardHeader>
            <CardContent>
              {recentLandscapes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No completed landscapes in the last 30 days</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {recentLandscapes.map((l, i) => (
                    <div
                      key={i}
                      className="p-4 rounded-lg border border-border hover:border-primary/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/products/${l.heroProductId}/landscapes`)}
                    >
                      <p className="text-sm font-normal text-foreground">{l.phrase}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{l.heroAsin}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-muted-foreground">{l.date}</span>
                        <Badge variant="outline" className="text-xs">
                          {l.keywordCount} keywords
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="w-full xl:w-[300px] xl:shrink xl:min-w-[220px]">
          <DashboardActivityPanel />
        </div>
      </div>
    </div>
  );
}
